import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { setAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Shield, RefreshCw } from 'lucide-react';

export const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' or 'otp'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [otpData, setOtpData] = useState({
    userId: '',
    email: '',
    otp: ['', '', '', '', '', ''],
  });
  const [resendCooldown, setResendCooldown] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const otpInputRefs = useRef([]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Focus first OTP input when step changes to OTP
  useEffect(() => {
    if (step === 'otp' && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [step]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await api.post('/auth/login', formData);

      if (data.requires_otp) {
        // Move to OTP step
        setOtpData({
          userId: data.user_id,
          email: data.email,
          otp: ['', '', '', '', '', ''],
        });
        setStep('otp');
        setResendCooldown(60); // 60 second cooldown before resend
        toast.success('Verification code sent to your email');
      } else if (data.access_token) {
        // 2FA disabled on this account — direct login.
        setAuth(data.access_token, data.user, data.accounts);
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otpData.otp];
    newOtp[index] = value.slice(0, 1);
    setOtpData({ ...otpData, otp: newOtp });

    // Auto-focus next input
    if (value.length === 1 && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value.length === 1 && index === 5) {
      const fullOtp = [...newOtp.slice(0, 5), value].join('');
      if (fullOtp.length === 6) {
        handleOtpSubmit(fullOtp);
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otpData.otp];
      newOtp[index] = '';
      setOtpData({ ...otpData, otp: newOtp });
      
      if (index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtpData({ ...otpData, otp: newOtp });
      handleOtpSubmit(pastedData);
    }
  };

  const handleOtpSubmit = async (otpCode = null) => {
    const code = otpCode || otpData.otp.join('');
    if (code.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', {
        user_id: otpData.userId,
        otp_code: code,
        trust_device: trustDevice,
      });

      setAuth(data.access_token, data.user, data.accounts);
      toast.success('Logged in successfully');
      
      // Navigate based on user type and default_page
      if (data.user.is_super_admin) {
        navigate(data.user.default_page || '/cockpit');
      } else if (data.accounts && data.accounts.length > 0) {
        localStorage.setItem('current_account', JSON.stringify(data.accounts[0]));
        navigate(data.user.default_page || '/bills/dashboard');
      } else {
        toast.error('No accounts found. Please contact administrator.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      // Clear OTP inputs on error
      setOtpData({ ...otpData, otp: ['', '', '', '', '', ''] });
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', otpData.userId);
      
      await api.post('/auth/resend-otp', formData);
      toast.success('New verification code sent');
      setResendCooldown(60);
      setOtpData({ ...otpData, otp: ['', '', '', '', '', ''] });
      otpInputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setOtpData({ userId: '', email: '', otp: ['', '', '', '', '', ''] });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-10 w-10" />
            <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              BillPortal
            </h1>
          </div>
          <p className="text-muted-foreground text-sm uppercase tracking-wide font-medium">
            PDF Bill Extraction & Review
          </p>
        </div>

        <div className="bg-card border border-border rounded-none p-8">
          {step === 'credentials' ? (
            <>
              <h2 className="text-2xl font-bold tracking-tight mb-6">Sign In</h2>

              <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wide font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    required
                    className="rounded-none mt-1"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wide font-medium">
                    Password
                  </Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    required
                    className="rounded-none mt-1"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="auth-submit-button"
                  className="w-full rounded-none font-bold uppercase tracking-wide"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Continue'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>

              <div className="mt-6 text-center text-xs text-muted-foreground">
                Contact your administrator for account access
              </div>
            </>
          ) : (
            <>
              <button
                onClick={handleBackToCredentials}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
                data-testid="back-to-login"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 bg-primary/10 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Verify Your Identity</h2>
                  <p className="text-sm text-muted-foreground">Two-factor authentication</p>
                </div>
              </div>

              <div className="bg-muted/30 p-4 mb-6 flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm">
                    We&apos;ve sent a 6-digit verification code to:
                  </p>
                  <p className="text-sm font-mono font-medium mt-1">{otpData.email}</p>
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-xs uppercase tracking-wide font-medium mb-3 block">
                  Enter Verification Code
                </Label>
                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otpData.otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold rounded-none"
                      data-testid={`otp-input-${index}`}
                    />
                  ))}
                </div>
              </div>

              <label className="mb-4 flex cursor-pointer items-start gap-2 select-none">
                <input
                  type="checkbox"
                  data-testid="trust-device-checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-300 text-primary focus:ring-2 focus:ring-primary/30"
                />
                <span className="text-sm text-slate-700">
                  Trust this device for 30 days
                  <span className="block text-xs text-muted-foreground">
                    Skip the verification code next time you sign in from this browser.
                  </span>
                </span>
              </label>

              <Button
                onClick={() => handleOtpSubmit()}
                data-testid="verify-otp-button"
                className="w-full rounded-none font-bold uppercase tracking-wide mb-4"
                disabled={loading || otpData.otp.some(d => d === '')}
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>

              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">
                  Code expires in 10 minutes
                </p>
                <button
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || loading}
                  className={`inline-flex items-center gap-2 text-sm ${
                    resendCooldown > 0 ? 'text-muted-foreground' : 'text-primary hover:underline'
                  }`}
                  data-testid="resend-otp-button"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
